import { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { MenuItem, Row } from '@tempmee/react';

interface IAccountMenuItemBaseProps {
  /** Visible label. */
  text: string;
  /** `data-cy` hook consumed by the E2E suite. */
  dataCy: string;
  /** Analytics/side-effect handler for links, or the action for a plain item. */
  onClick?: () => void;
  /** Optional trailing content, e.g. a status warning icon. */
  endAdornment?: ReactNode;
}

/** In-portal row: client-side route, or action-only when `to` is omitted. */
type TInternalAccountMenuItemProps = IAccountMenuItemBaseProps & {
  /** Route to navigate to (client-side). Omit for an action-only item. */
  to?: string;
  externalHref?: never;
};

/** Off-site row: absolute URL rendered as a real link. */
type TExternalAccountMenuItemProps = IAccountMenuItemBaseProps & {
  /** Absolute URL for a row that leaves the portal; renders an `<a target="_blank">`. */
  externalHref: string;
  to?: never;
};

/**
 * The two forms are mutually exclusive — mirroring the design system's own
 * `MenuItemProps` — so a row can never declare both destinations and have one
 * of them silently ignored.
 */
type TAccountMenuItemProps = TInternalAccountMenuItemProps | TExternalAccountMenuItemProps;

const MENU_ITEM_CLASS_NAME = 'font-bold hover:bg-transparent';

/**
 * A single row in the Account menu, rendered with the design-system `MenuItem`.
 * In-portal rows render a `<button>` (not an `<a href>`) so navigation stays
 * client-side via `useNavigate` instead of triggering a full page reload; a row
 * pointing off-site uses the DS link form so it behaves like a real link.
 */
export const AccountMenuItem = ({
  text,
  dataCy,
  to,
  externalHref,
  onClick,
  endAdornment,
}: TAccountMenuItemProps) => {
  const navigate = useNavigate();

  const handleClick = () => {
    onClick?.();
    if (to) navigate(to);
  };

  const label = endAdornment ? (
    <Row align="center" gap={2}>
      {text}
      {endAdornment}
    </Row>
  ) : (
    text
  );

  if (externalHref) {
    return (
      <MenuItem
        className={MENU_ITEM_CLASS_NAME}
        data-cy={dataCy}
        href={externalHref}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
      >
        {label}
      </MenuItem>
    );
  }

  return (
    <MenuItem className={MENU_ITEM_CLASS_NAME} data-cy={dataCy} onClick={handleClick}>
      {label}
    </MenuItem>
  );
};
